import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useEffect } from "react";
import { useContactCenterStyles } from "@components/virtual-receptionist/theming/useContactCenterStyles";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import { TranscriptionRecord } from "../actions/transcription.action";
import { useTranscription } from "../hooks/useTranscription";

type Props = {
    uid: string;
    open: boolean;
    seed?: TranscriptionRecord | null;
};

const formatTimestamp = (seconds?: number) => {
    if (seconds === undefined || seconds === null) return "";

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export default function TranscriptPanel({ uid, open, seed }: Props) {
    const styles = useContactCenterStyles();
    const { showSnackbar } = useGlobalSnackbar();

    const { transcription, status, isProcessing, isLoading, hasStartError, canAutoStart, start } =
        useTranscription({
            uid,
            enabled: open,
            seed,
        });

    useEffect(() => {
        if (open && status === "none" && canAutoStart) {
            start();
        }
    }, [open, status, canAutoStart, start]);

    const handleCopy = () => {
        if (!transcription?.text) return;

        navigator.clipboard
            .writeText(transcription.text)
            .then(() => showSnackbar("Transcript copied", "success"))
            .catch(() => showSnackbar("Could not copy the transcript", "error"));
    };

    const sentences = transcription?.sentences ?? [];
    const failed = status === "failed" || hasStartError;

    return (
        <Box
            sx={{
                ...styles.inset,
                mt: 1.5,
                p: 1.5,
                borderRadius: "12px",
                maxHeight: 260,
                overflowY: "auto",
            }}
        >
            {(isProcessing || isLoading) && !failed && (
                <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />

                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
                        Transcribing this recording...
                    </Typography>
                </Box>
            )}

            {failed && (
                <Box display="flex" flexDirection="column" gap={1}>
                    <Typography sx={{ fontSize: "0.8rem", color: "error.main", fontWeight: 600 }}>
                        {transcription?.error || "The transcription could not be generated"}
                    </Typography>

                    <Button
                        size="small"
                        variant="outlined"
                        onClick={start}
                        sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, alignSelf: "flex-start" }}
                    >
                        Retry
                    </Button>
                </Box>
            )}

            {status === "completed" && !failed && (
                <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ ...styles.timeLabel, textTransform: "uppercase", fontWeight: 700 }}>
                            Transcript
                        </Typography>

                        <Tooltip title="Copy transcript">
                            <IconButton size="small" onClick={handleCopy}>
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {sentences.length > 0 ? (
                        sentences.map((sentence, index) => (
                            <Box key={`${uid}-sentence-${index}`} display="flex" gap={1}>
                                <Typography sx={{ ...styles.timeLabel, minWidth: 34, pt: "2px" }}>
                                    {formatTimestamp(sentence.startTime)}
                                </Typography>

                                <Typography sx={{ fontSize: "0.8rem", lineHeight: 1.5 }}>
                                    {sentence.text}
                                </Typography>
                            </Box>
                        ))
                    ) : (
                        <Typography sx={{ fontSize: "0.8rem", lineHeight: 1.5 }}>
                            {transcription?.text || "No speech was detected in this recording"}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}
